// Copyright (c) StrangeBee 2024

import PreviewButton from '@Atoms/Buttons/PreviewButton/PreviewButton';
import { IconLabel } from '@Atoms/Info';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import client from '@Client';
import { useLocalMessage } from '@Helpers/locale';
import { handleNotificationError, openMessage } from '@Helpers/notification';
import { useDrawer } from '@Hooks/useDrawer';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { THEditable } from '@Molecules/Inputs/THEditable/THEditable';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { DisplayEventProps } from '@Organisms/Timeline/DisplayEvent';
import { EventCard } from '@Organisms/Timeline/EventCard';
import { ttpEventList } from '@Organisms/TTP/eventList';
import { TTPDrawer } from '@Organisms/TTP/TTPDrawer/TTPDrawer';
import { useListAllUsers } from '@Queries/userQueries';
import React, { ReactElement } from 'react';
import { ProcedureOutput } from 'thehive-sdk';

import './ProcedureContent.scss';

export const ProcedureContent = (props: DisplayEventProps): ReactElement => {
    const i = useLocalMessage();
    const { data: userList = [] } = useListAllUsers();
    const procedure = props.currentEvent.details?.procedure;
    const { refetch } = props;

    const { visible, setVisible } = useDrawer<ProcedureOutput>({});

    const { _createdAt, _createdBy, patternId, patternName } = procedure || {};

    return (
        <EventCard {...props}>
            <div className="procedure-content card__content">
                <div className="procedure-content__header card__header">
                    <div className="procedure-content__title">{`${patternId} - ${patternName}`}</div>
                </div>
                {_createdBy && (
                    <THEditable
                        name="_createdBy"
                        className="card-info__user"
                        initialValues={{ _createdBy }}
                        label={i('common.created-by')}
                    >
                        <THSelect disabled borderless>
                            {userList?.map(({ _id, name, avatar, login }) => (
                                <THSelect.Option key={_id} value={login}>
                                    <IconLabel
                                        key={_id}
                                        icon={<UserAvatar name={name} avatar={avatar} />}
                                        label={name}
                                    />
                                </THSelect.Option>
                            ))}
                        </THSelect>
                    </THEditable>
                )}
                {_createdAt && (
                    <THEditable
                        name="_createdAt"
                        className="card-info__date"
                        initialValues={{ _createdAt }}
                        label={i('common.created-date')}
                    >
                        <THDatePicker disabled borderless />
                    </THEditable>
                )}

                {procedure && (
                    <div className="card__preview">
                        <PreviewButton onClick={() => setVisible(true)} />
                    </div>
                )}

                {procedure && (
                    <TTPDrawer
                        onVisibleChange={setVisible}
                        visible={visible}
                        data={{
                            ...procedure,
                            extraData: { ...procedure.extraData, pattern: { name: patternName } },
                        }}
                        eventList={ttpEventList({
                            controller: client.ProcedureController,
                            locale: i,
                            onError: handleNotificationError,
                            onSuccess: (_, message) => {
                                message && openMessage('success', message);
                                refetch?.();
                            },
                        })}
                    />
                )}
            </div>
        </EventCard>
    );
};
